import * as React from 'react';

import { TranslationIcon } from '@coursera/cds-icons';

import { type UnappliedFiltersByFacet } from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import { type FilterChipDropdownProps } from 'bundles/search-common/components/experiments/filters2024/FilterChipDropdown';
import { deconstructFilter } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export const FILTER_DROPDOWN_KEYS = ['productTypeDescription', 'productDifficultyLevel', 'topic', 'language'];

export const getFilterDropdownPropsMap = (): Record<string, Partial<FilterChipDropdownProps>> => ({
  topic: {
    triggerLabel: _t('Subject'),
    triggerPrefix: (
      <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0 12.5749V6.73989C0 6.40194 0.124438 6.11978 0.373315 5.89341C0.622057 5.6669 0.915872 5.56712 1.25476 5.59407C2.30888 5.75576 3.30984 6.06905 4.25765 6.53393C5.20559 6.9988 6.07707 7.59142 6.87207 8.31177C7.66708 7.59142 8.53855 6.9988 9.48649 6.53393C10.4343 6.06905 11.4353 5.75576 12.4894 5.59407C12.8283 5.56712 13.1221 5.6669 13.3708 5.89341C13.6197 6.11978 13.7441 6.40194 13.7441 6.73989V12.5749C13.7441 12.8972 13.6376 13.1737 13.4246 13.4044C13.2116 13.6349 12.9439 13.7637 12.6216 13.7906C11.7177 13.9306 10.8602 14.1543 10.0492 14.4617C9.23815 14.7689 8.46154 15.1837 7.71936 15.7061C7.59189 15.8066 7.45802 15.8808 7.31774 15.9285C7.17734 15.9762 7.02858 16 6.87146 16C6.71435 16 6.56579 15.9762 6.42579 15.9285C6.28592 15.8808 6.15225 15.8066 6.02478 15.7061C5.2826 15.1837 4.50727 14.7689 3.69879 14.4617C2.89031 14.1543 2.03157 13.9306 1.12257 13.7906C0.800259 13.7637 0.532585 13.6349 0.319551 13.4044C0.106517 13.1737 0 12.8972 0 12.5749ZM6.87207 14.8138C7.72097 14.1857 8.62377 13.6819 9.58047 13.3023C10.5372 12.9227 11.5208 12.672 12.5314 12.55V6.81608C11.5115 6.99125 10.524 7.3385 9.56875 7.85781C8.61367 8.37699 7.71478 9.05793 6.87207 9.90063C6.02937 9.05793 5.13047 8.37699 4.17539 7.85781C3.22017 7.3385 2.23261 6.99125 1.21272 6.81608V12.55C2.22332 12.672 3.20697 12.9227 4.16367 13.3023C5.12037 13.6819 6.02317 14.1857 6.87207 14.8138ZM6.87207 5.84591C6.06831 5.84591 5.38022 5.55971 4.80782 4.9873C4.23542 4.4149 3.94922 3.72675 3.94922 2.92285C3.94922 2.11909 4.23542 1.43101 4.80782 0.858604C5.38022 0.286201 6.06831 0 6.87207 0C7.67583 0 8.36392 0.286201 8.93632 0.858604C9.50872 1.43101 9.79492 2.11909 9.79492 2.92285C9.79492 3.72675 9.50872 4.4149 8.93632 4.9873C8.36392 5.55971 7.67583 5.84591 6.87207 5.84591ZM6.87227 4.63319C7.34267 4.63319 7.7453 4.4657 8.08014 4.13072C8.41498 3.79574 8.58241 3.39305 8.58241 2.92265C8.58241 2.45225 8.41492 2.04963 8.07994 1.71478C7.74496 1.38007 7.34227 1.21272 6.87187 1.21272C6.40147 1.21272 5.99885 1.38021 5.664 1.71519C5.32916 2.05017 5.16173 2.45286 5.16173 2.92326C5.16173 3.39366 5.32922 3.79621 5.6642 4.13092C5.99918 4.46577 6.40187 4.63319 6.87227 4.63319Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  productTypeDescription: {
    triggerLabel: _t('Product Type'),
    triggerPrefix: (
      <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3.44453 11.2185C3.20232 11.0816 3.00985 10.8956 2.8671 10.6605C2.7242 10.4253 2.65275 10.1616 2.65275 9.86915V6.2082L1.14923 5.37488C1.01231 5.29257 0.910569 5.19555 0.843998 5.08381C0.777427 4.97207 0.744141 4.84798 0.744141 4.71153C0.744141 4.57508 0.777427 4.45115 0.843998 4.33972C0.910569 4.2283 1.01231 4.13143 1.14923 4.04912L7.58941 0.536426C7.70367 0.472845 7.8217 0.427441 7.94351 0.400214C8.06533 0.372988 8.19029 0.359375 8.31839 0.359375C8.4465 0.359375 8.57146 0.372988 8.69327 0.400214C8.81508 0.427441 8.93335 0.472687 9.04808 0.535954L16.3393 4.48868C16.4724 4.56737 16.5732 4.66613 16.6417 4.78495C16.71 4.90377 16.7441 5.03195 16.7441 5.1695V10.2686C16.7441 10.4023 16.6989 10.5145 16.6084 10.605C16.5178 10.6956 16.4055 10.7409 16.2715 10.7409C16.1374 10.7409 16.0253 10.6956 15.9351 10.605C15.845 10.5145 15.7999 10.4023 15.7999 10.2686V5.24221L13.984 6.2082V9.86915C13.984 10.1616 13.9126 10.4253 13.7697 10.6605C13.6269 10.8956 13.4345 11.0816 13.1923 11.2185L9.05209 13.4593C8.93469 13.5258 8.81508 13.5727 8.69327 13.6C8.57146 13.6272 8.4465 13.6408 8.31839 13.6408C8.19029 13.6408 8.06533 13.6272 7.94351 13.6C7.8217 13.5727 7.7021 13.5258 7.58469 13.4593L3.44453 11.2185ZM8.1005 8.09675C8.18517 8.14522 8.26079 8.16946 8.32736 8.16946C8.39409 8.16946 8.46979 8.14522 8.55446 8.09675L14.7685 4.712L8.55446 1.3452C8.46979 1.29688 8.39409 1.27272 8.32736 1.27272C8.26079 1.27272 8.18517 1.29688 8.1005 1.3452L1.8683 4.712L8.1005 8.09675ZM8.08232 12.6602C8.16699 12.7087 8.24568 12.7329 8.31839 12.7329C8.3911 12.7329 8.46979 12.7087 8.55446 12.6602L12.7673 10.3831C12.8643 10.3225 12.9339 10.2528 12.9763 10.1741C13.0186 10.0955 13.0398 9.9956 13.0398 9.87458V6.72755L9.05752 8.90127C8.94012 8.96784 8.82082 9.01474 8.69964 9.04197C8.57862 9.06919 8.45153 9.08281 8.31839 9.08281C8.18525 9.08281 8.05817 9.06919 7.93714 9.04197C7.81596 9.01474 7.69667 8.96784 7.57926 8.90127L3.59703 6.72755V9.87458C3.59703 9.97152 3.61819 10.0654 3.66053 10.1562C3.70286 10.2469 3.7725 10.3225 3.86945 10.3831L8.08232 12.6602Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  productDifficultyLevel: {
    triggerLabel: _t('Difficulty'),
    triggerPrefix: (
      <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.63303 14.222H5.78681V7.11089H1.63303V14.222ZM6.6757 14.222H10.8126V1.77756H6.6757V14.222ZM11.7015 14.222H15.8553V8.88867H11.7015V14.222ZM0.744141 13.6749V7.65801C0.744141 7.26304 0.884733 6.92497 1.16592 6.64378C1.4471 6.3626 1.78518 6.22201 2.18014 6.22201H5.78681V2.32467C5.78681 1.92971 5.9274 1.59163 6.20859 1.31045C6.48977 1.02926 6.82784 0.888672 7.22281 0.888672H10.2655C10.6604 0.888672 10.9985 1.02926 11.2797 1.31045C11.5609 1.59163 11.7015 1.92971 11.7015 2.32467V7.99978H15.3081C15.7031 7.99978 16.0412 8.14038 16.3224 8.42156C16.6035 8.70275 16.7441 9.04082 16.7441 9.43578V13.6749C16.7441 14.0699 16.6035 14.4079 16.3224 14.6891C16.0412 14.9703 15.7031 15.1109 15.3081 15.1109H2.18014C1.78518 15.1109 1.4471 14.9703 1.16592 14.6891C0.884733 14.4079 0.744141 14.0699 0.744141 13.6749Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  language: {
    triggerLabel: _t('Language'),
    triggerPrefix: <TranslationIcon size="small" />,
  },
});

export function getCountsPerKey(userFilters?: string[][]) {
  const counts: Record<string, number> = {};

  userFilters?.forEach((subArray) => {
    subArray.forEach((item) => {
      const { facetName } = deconstructFilter(item);
      if (counts[facetName]) {
        counts[facetName] += 1;
      } else {
        counts[facetName] = 1;
      }
    });
  });

  return counts;
}

export function getSingleFilterValueForKey(
  filterKey: string | undefined,
  userFilters: string[][] | undefined
): string | undefined {
  if (!filterKey || !userFilters) {
    return undefined;
  }

  for (const subArray of userFilters) {
    for (const item of subArray) {
      const { facetName, facetValue } = deconstructFilter(item);
      if (facetName === filterKey) {
        return facetValue;
      }
    }
  }

  return undefined;
}

type QuickFilterProps = {
  label: string;
  prefix?: React.ReactNode;
  values: string[];
};

export const getQuickFilterProps = (): QuickFilterProps[] => {
  return [
    {
      label: _t('Courses'),
      prefix: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.01574 1.5C3.87248 1.51095 3.73873 1.57658 3.64236 1.68354C3.54339 1.79341 3.49188 1.93795 3.49909 2.08565L3.49968 2.09782V12.438L3.49839 12.4559C3.48544 12.6358 3.52749 12.8155 3.61897 12.971C3.71045 13.1265 3.84704 13.2506 4.01062 13.3267L4.02143 13.3317L4.03199 13.3372C4.0895 13.3674 4.15233 13.3861 4.21699 13.3922L4.23351 13.3938L10.746 14.4522L12.4997 11.541V1.5H4.01574ZM3.99968 1V0.5H13.4997V11.819L11.2534 15.5478L4.1042 14.3859C3.92069 14.3662 3.74237 14.3128 3.57822 14.2284C3.2355 14.0663 2.94934 13.8049 2.75704 13.478C2.56604 13.1533 2.47651 12.7791 2.49968 12.4034V2.12101C2.48333 1.71422 2.62667 1.31694 2.8994 1.01421C3.17511 0.708167 3.56068 0.523596 3.97197 0.500768L3.99968 1Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.49945 2.12525C3.49489 2.27467 3.5482 2.42008 3.64826 2.53114C3.74832 2.64221 3.8874 2.71035 4.03648 2.72136L4.05699 2.72287L11.4997 3.89244V15H10.4997V4.74756L3.94045 3.71683C3.54281 3.68178 3.17285 3.49746 2.9053 3.20048C2.63272 2.89792 2.48749 2.5018 2.49991 2.09475L3.49945 2.12525Z"
            fill="currentColor"
          />
        </svg>
      ),
      values: ['productTypeDescription:Courses'],
    },
    {
      label: _t('Professional Certificates'),
      prefix: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.5 3.5H15.5V13.5H0.5V3.5ZM1.5 4.5V12.5H14.5V4.5H1.5Z"
            fill="currentColor"
          />
          <path fillRule="evenodd" clipRule="evenodd" d="M3.61 13V4H4.61V13H3.61Z" fill="currentColor" />
          <path fillRule="evenodd" clipRule="evenodd" d="M11.39 13V4H12.39V13H11.39Z" fill="currentColor" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.9403 2.70726C6.74723 2.86622 6.5 3.21019 6.5 4H5.5C5.5 2.97981 5.83277 2.32378 6.3047 1.93524C6.75588 1.56378 7.25849 1.5 7.5 1.5H8.5C8.74151 1.5 9.24412 1.56378 9.6953 1.93524C10.1672 2.32378 10.5 2.97981 10.5 4H9.5C9.5 3.21019 9.25277 2.86622 9.0597 2.70726C8.84588 2.53122 8.59849 2.5 8.5 2.5H7.5C7.40151 2.5 7.15412 2.53122 6.9403 2.70726Z"
            fill="currentColor"
          />
        </svg>
      ),
      values: ['productTypeDescription:Professional Certificates'],
    },
    {
      label: _t('Degrees'),
      prefix: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.90052 2.4375L14.7659 6.00084L7.90052 9.56417L1.03516 6.00084L7.90052 2.4375ZM3.20588 6.00084L7.90052 8.4375L12.5952 6.00084L7.90052 3.56417L3.20588 6.00084Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.50052 9.67084V7.50084H5.50052V9.67084C5.50052 10.0119 5.69839 10.3746 6.12724 10.6728C6.55433 10.9699 7.17418 11.1708 7.89052 11.1708C8.60686 11.1708 9.22671 10.9699 9.65379 10.6728C10.0826 10.3746 10.2805 10.0119 10.2805 9.67084V7.50084H11.2805V9.67084C11.2805 10.4298 10.8384 11.0671 10.2247 11.4938C9.60933 11.9218 8.78418 12.1708 7.89052 12.1708C6.99686 12.1708 6.17171 11.9218 5.55629 11.4938C4.94265 11.0671 4.50052 10.4298 4.50052 9.67084Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.45681 5.77035C7.58411 5.5253 7.88595 5.42984 8.131 5.55713L13.271 8.22713C13.4366 8.31315 13.5405 8.48423 13.5405 8.67084V10.3308C13.5405 10.607 13.3167 10.8308 13.0405 10.8308C12.7644 10.8308 12.5405 10.607 12.5405 10.3308V8.97454L7.67003 6.44454C7.42498 6.31725 7.32952 6.0154 7.45681 5.77035Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.3709 9.72387C12.5776 9.62172 12.805 9.56859 13.0355 9.56859C13.266 9.56859 13.4935 9.62172 13.7001 9.72387C13.9068 9.82601 14.0872 9.97442 14.2272 10.1576C14.3672 10.3407 14.4631 10.5537 14.5075 10.7799C14.5471 10.9821 14.5447 11.19 14.5005 11.3907V14.5008H11.5705V11.3907C11.5264 11.19 11.5239 10.9821 11.5636 10.7799C11.6079 10.5537 11.7039 10.3407 11.8439 10.1576C11.9839 9.97442 12.1642 9.82601 12.3709 9.72387ZM13.0355 10.5686C12.9587 10.5686 12.8829 10.5863 12.814 10.6203C12.7451 10.6544 12.685 10.7039 12.6383 10.7649C12.5916 10.826 12.5597 10.8969 12.5449 10.9723C12.5301 11.0478 12.5329 11.1256 12.553 11.1997L12.5705 11.2641V13.5008H13.5005V11.2641L13.518 11.1997C13.5382 11.1256 13.541 11.0478 13.5262 10.9723C13.5114 10.8969 13.4794 10.826 13.4327 10.7649C13.3861 10.7039 13.3259 10.6544 13.2571 10.6203C13.1882 10.5863 13.1124 10.5686 13.0355 10.5686Z"
            fill="currentColor"
          />
        </svg>
      ),
      values: ['productTypeDescription:Degrees'],
    },
    {
      label: _t('Beginner'),
      values: ['productDifficultyLevel:Beginner'],
    },
    {
      label: _t('Advanced'),
      values: ['productDifficultyLevel:Advanced'],
    },
  ];
};

export const getFiltersByFacet = (facetFilters: string[][] | undefined): UnappliedFiltersByFacet => {
  if (facetFilters) {
    return facetFilters.reduce((accumulatedFacetValues, currentFacetValue) => {
      const facet = currentFacetValue[0].split(':')[0];
      return { ...accumulatedFacetValues, [facet]: currentFacetValue };
    }, {});
  }
  return {};
};
